import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { fetchPickerQueue, updatePickerQueue } from "../../services"
import { Button, Loader, Notification } from "../../components"
import { MenuIcon } from "@heroicons/react/outline"
import { PICK_TYPE_STYLES } from "../../utils/pickingStyles"

export const PickerQueueManager = ({ pickerId, pickerName, onClose }) => {
    const { i18n } = useTranslation()
    const [queue, setQueue] = useState([])
    const [draggedIndex, setDraggedIndex] = useState(null)
    const [busy, setBusy] = useState(false)
    const [notification, setNotification] = useState(null)
    const [hasChanges, setHasChanges] = useState(false)

    const { data, isLoading, isError, error, refetch } = useQuery(
        ["picker_queue", pickerId],
        () => fetchPickerQueue(pickerId),
        { enabled: !!pickerId, refetchOnWindowFocus: false, retry: false }
    )

    // Snapshot of the queue as loaded from the server. hasChanges is recomputed
    // against this ref every time queue mutates so cancelled drags do not leave
    // the Save button enabled.
    const loadedIdsRef = useRef("")

    const computeHasChanges = (items) => {
        return JSON.stringify(items.map(t => t.id)) !== loadedIdsRef.current
    }

    useEffect(() => {
        if (data) {
            // Backend may return { sessions: [{ tasks: [...] }] } or { tasks: [...] }
            // Flatten to a single ordered list
            let items = []
            if (data.sessions && Array.isArray(data.sessions)) {
                for (const session of data.sessions) {
                    if (Array.isArray(session.tasks)) {
                        items.push(...session.tasks.map(t => ({ ...t, session_id: session.id })))
                    }
                }
            } else if (Array.isArray(data.tasks)) {
                items = data.tasks
            } else if (Array.isArray(data)) {
                items = data
            }
            setQueue(items)
            loadedIdsRef.current = JSON.stringify(items.map(t => t.id))
            setHasChanges(false)
        }
    }, [data])

    const dragStartQueueRef = useRef(null)
    const dropCommittedRef = useRef(false)

    const handleDragStart = (index) => {
        dragStartQueueRef.current = queue
        dropCommittedRef.current = false
        setDraggedIndex(index)
    }

    const handleDragOver = (e, index) => {
        e.preventDefault()
        if (draggedIndex === null || draggedIndex === index) return
        const newQueue = [...queue]
        const [moved] = newQueue.splice(draggedIndex, 1)
        newQueue.splice(index, 0, moved)
        setQueue(newQueue)
        setDraggedIndex(index)
        setHasChanges(computeHasChanges(newQueue))
    }

    const handleDrop = () => {
        dropCommittedRef.current = true
    }

    const handleDragEnd = () => {
        // If drop did not commit (Esc, dropped outside), restore the queue to
        // the state it had when the drag started.
        if (!dropCommittedRef.current && dragStartQueueRef.current) {
            const restored = dragStartQueueRef.current
            setQueue(restored)
            setHasChanges(computeHasChanges(restored))
        }
        dragStartQueueRef.current = null
        dropCommittedRef.current = false
        setDraggedIndex(null)
    }

    const handleSave = async () => {
        setBusy(true)
        try {
            const orderedTaskIds = queue.map(t => t.id)
            await updatePickerQueue(pickerId, orderedTaskIds)
            setNotification({ type: "success", message: i18n.t("picking_tasks.multi_picker.queue_save_success") })
            loadedIdsRef.current = JSON.stringify(orderedTaskIds)
            setHasChanges(false)
            refetch()
        } catch (error) {
            const msg = error.response?.data?.error?.message || error.response?.data?.error || i18n.t("picking_tasks.multi_picker.queue_save_error")
            setNotification({ type: "error", message: msg })
        }
        setBusy(false)
    }

    if (isLoading) return <Loader show={true} />

    return (
        <div className="p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-1">
                {i18n.t("picking_tasks.multi_picker.picker_queue")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{pickerName}</p>

            {isError ? (
                <div className="p-6 text-center text-gray-500">
                    {[401, 403].includes(error?.response?.status)
                        ? i18n.t("picking_tasks.multi_picker.queue_unauthorized")
                        : i18n.t("picking_tasks.multi_picker.queue_error")}
                </div>
            ) : queue.length === 0 ? (
                <div className="p-6 text-center text-gray-500">{i18n.t("picking_tasks.multi_picker.queue_empty")}</div>
            ) : (
                <>
                    <p className="text-xs text-gray-500 mb-2">{i18n.t("picking_tasks.multi_picker.queue_reorder")}</p>
                    <ul className="border border-gray-200 rounded divide-y divide-gray-200 max-h-60vh overflow-y-auto">
                        {queue.map((task, index) => (
                            <li
                                key={task.id}
                                draggable
                                onDragStart={() => handleDragStart(index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                                className={`flex items-center gap-3 px-3 py-2 cursor-move hover:bg-gray-50 ${draggedIndex === index ? "opacity-50" : ""}`}
                            >
                                <MenuIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-xs font-mono text-gray-500 w-6">{index + 1}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PICK_TYPE_STYLES[task.pick_type] || "bg-gray-100 text-gray-800"}`}>
                                    {i18n.t(`picking_tasks.pick_types.${task.pick_type}`)}
                                </span>
                                <span className="font-mono text-sm">{task.location_code}</span>
                                <span className="text-sm text-gray-500 ml-auto">{task.expected_quantity} {i18n.t("picking_tasks.multi_picker.pieces_short")}</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            <div className="mt-4 flex justify-between">
                <Button type="secondary" onClick={onClose} disabled={busy}>{i18n.t("picking_tasks.multi_picker.close")}</Button>
                <Button onClick={handleSave} disabled={busy || !hasChanges}>
                    {i18n.t("picking_tasks.multi_picker.queue_save")}
                </Button>
            </div>

            <Notification
                show={!!notification}
                setShow={() => setNotification(null)}
                type={notification?.type || "success"}
                title={notification?.message || ""}
            />
        </div>
    )
}
